import { useState, useEffect, useRef } from "react";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import {
  UserPlus,
  X,
  ArrowLeft,
  Lock,
  Eye,
  EyeOff,
  Pencil,
} from "lucide-react";
import PhoneInput from "../../components/common/inputfields/PhoneInput";
import { useDispatch, useSelector } from "react-redux";
import {
  forgetPassword,
  loginUser,
  sentLoginOtpCode,
  signupOtpVerification,
  signupOtpVerificationDeliveryPartner,
  signUpUser,
  updateNewPassword,
  verifyOtp,
  verifyResetCode,
} from "../../store/thunk/authThunk";
import ButtonLoader from "../loader/ButtonLoader";
import { selectAuthLoading } from "../../store/reducer/authSlice";
import toast from "react-hot-toast";
import { useNavigate, useLocation } from "react-router-dom";
import { addToCart, fetchCartInfo } from "../../store/thunk/cartThunk";
import { clearLocalCart } from "../../store/reducer/cartSlice";
import { fetchUserProfile } from "../../store/thunk/authThunk";
import { formatPhoneNumber } from "../../hooks/commonFunction";
import { setPartnerInfo } from "../../store/reducer/deliveryPartnerSlice";
import { useGoogleLogin } from "../../hooks/googleAuth";
import { getSecureItem } from "../../utils/secureStorage";

// ============================================
// Reusable Modal Component
// ============================================
export const Modal = ({ isOpen, onClose, children, className = "" }) => {
  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      {/* Backdrop overlay */}
      <DialogBackdrop
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal content wrapper */}
      <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4">
        <DialogPanel
          className={`relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg ${className}`}
        >
          {children}
        </DialogPanel>
      </div>
    </Dialog>
  );
};

// ============================================
// Login Modal Component
// ============================================
export const LoginModal = ({
  isOpen,
  onClose,
  onSwitchToSignup,
  onOTPVerification,
  onLoginSuccess,
  isCheckoutMode = false,
  phone_code,
}) => {
  // Local state for form data
  const [formData, setFormData] = useState({ phone_number: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  // Redux hooks
  const loading = useSelector(selectAuthLoading);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Google login hook
  const { handleGoogleLogin } = useGoogleLogin();

  // Custom Google login handler for modal context
  const handleModalGoogleLogin = async () => {
    try {
      await handleGoogleLogin("/");
      onClose();
      if (onLoginSuccess) onLoginSuccess();
    } catch (error) {
      console.error("Modal Google login error:", error);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Validate login form
  const validateForm = () => {
    const newErrors = {};
    if (!formData.phone_number || formData.phone_number.length < 10) {
      newErrors.phone_number = "Please enter a valid mobile number";
    }
    if (!formData.password || formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle login with password
  const handleLogin = () => {
    if (!validateForm()) return;

    const loginPromise = dispatch(loginUser(formData)).unwrap();

    toast.promise(
      loginPromise.then(async (res) => {
        // If user is not verified, send OTP
        if (res.is_verified === false) {
          const onlyDigits = res.phone_number.replace(/\D/g, "");
          try {
            await dispatch(
              sentLoginOtpCode({
                phone_number: onlyDigits,
                phone_code: phone_code,
                verification_type: "login",
              })
            ).unwrap();

            onClose();
            onOTPVerification({
              phone_number: onlyDigits,
              user_id: res.user_id,
              verification_type: "login",
              mode: "login-with-otp",
            });

            return "OTP sent. Please verify...";
          } catch (otpError) {
            throw new Error(otpError.message || "Failed to send OTP.");
          }
        }

        // Check for login failure
        if (res.status === 0) {
          throw new Error(res.message || "Login failed.");
        }

        // Removed legacy pending_subscriptions checks as we now use localCart for subscriptions

        // Redirect superadmin to orders list
        if (res?.details?.roles == 1) {
          navigate("/");
          onClose();
          setFormData({ phone_number: "", password: "" });
          setErrors({});
          return res.message || "Login successful!";
        }

        // Handle checkout mode login
        if (isCheckoutMode) {
          onLoginSuccess && onLoginSuccess();
        } else {
          // Sync local cart to server
          const localCart = getSecureItem("localCart");

          if (localCart?.item_info) {
            const addCartPromises = Object.values(localCart.item_info).map(
              (item) => {
                const payload = {
                  product_id: item.product_id,
                  product_quantity: item.product_quantity,
                  preferences: item.preferences || item.product_preference,
                };

                // Add subscription fields if it's a subscription item
                if (item.enable_subscription) {
                  payload.enable_subscription = true;
                  payload.subscription_frequency = item.subscription_frequency;
                  payload.subscription_preferences =
                    item.subscription_preferences;
                  payload.subscription_cuts = item.subscription_cuts;
                  payload.subscription_custom_notes =
                    item.subscription_custom_notes;
                }

                return dispatch(addToCart(payload)).unwrap();
              }
            );

            return Promise.all(addCartPromises)
              .then(() => {
                onClose();
                setFormData({ phone_number: "", password: "" });
                setErrors({});
                dispatch(clearLocalCart());
                dispatch(fetchCartInfo());
                return res.message || "Login successful!";
              })
              .catch(() => {
                onClose();
                setFormData({ phone_number: "", password: "" });
                setErrors({});
                return res.message || "Login successful!";
              });
          } else {
            // No cart to sync, just close modal
            onClose();
            setFormData({ phone_number: "", password: "" });
            setErrors({});
            return res.message || "Login successful!";
          }
        }
      }),
      {
        loading: "Logging in...",
        success: (msg) => msg,
        error: (err) => err.message || "Login failed. Please try again.",
      },
      {
        success: { duration: 3000 },
        error: { duration: 4000 },
      }
    );
  };

  // Handle send OTP for login
  const handleSendOTP = () => {
    if (!formData.phone_number || formData.phone_number.length < 10) {
      setErrors({ phone_number: "Please enter a valid mobile number" });
      return;
    }

    const onlyDigits = formData.phone_number.replace(/\D/g, "");
    const otpPromise = dispatch(
      sentLoginOtpCode({
        phone_number: onlyDigits,
        phone_code: phone_code,
        verification_type: "login",
      })
    ).unwrap();

    toast.promise(
      otpPromise.then((res) => {
        if (res.status === 1) {
          onClose();
          onOTPVerification({
            phone_number: onlyDigits,
            user_id: res.user_id,
            verification_type: "login",
            mode: "login-with-otp",
          });
          return "OTP sent successfully!";
        } else {
          throw new Error(res.message || "Failed to send OTP");
        }
      }),
      {
        loading: "Sending OTP...",
        success: (msg) => msg,
        error: (err) => err.message || "Failed to send OTP",
      }
    );
  };

  // Handle forgot password flow
  const handleForgotPassword = () => {
    if (!formData.phone_number || formData.phone_number.length < 10) {
      setErrors({ phone_number: "Please enter a valid mobile number" });
      return;
    }

    const onlyDigits = formData.phone_number.replace(/\D/g, "");
    const otpPromise = dispatch(
      forgetPassword({ phone_number: onlyDigits })
    ).unwrap();

    toast.promise(
      otpPromise.then((res) => {
        if (res.status === 1) {
          onClose();
          onOTPVerification({
            phone_number: onlyDigits,
            user_id: res.user_id,
            mode: "forgot-password",
          });
          return "OTP sent successfully!";
        } else {
          throw new Error(res.message || "Failed to send OTP");
        }
      }),
      {
        loading: "Sending OTP...",
        success: (msg) => msg,
        error: (err) => err.message || "Failed to send OTP",
      }
    );
  };

  // Handle Enter key press to submit login
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !loading) {
      handleLogin();
    }
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({ phone_number: "", password: "" });
      setErrors({});
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      {/* Modal header */}
      <div className="bg-red-gradient text-white p-4 rounded-t-lg flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <UserPlus className="w-5 h-5" />
          <h2 className="text-lg font-semibold">Login</h2>
        </div>
        <button onClick={onClose} className="p-1 rounded">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Modal body */}
      <div className="p-6">
        <div className="space-y-4">
          {/* Phone number field */}
          <div className="pb-5">
            <div className="relative">
              <PhoneInput
                className="border-b border-gray-300 font-medium bg-white w-full"
                value={formData.phone_number}
                onChange={(val) =>
                  setFormData((prev) => ({ ...prev, phone_number: val }))
                }
                onKeyPress={handleKeyPress}
              />
            </div>
            {errors.phone_number && (
              <p className="text-red-500 text-xs mt-1">{errors.phone_number}</p>
            )}
            <button
              onClick={handleSendOTP}
              className="text-black text-sm font-semibold mt-2 float-right"
            >
              Send OTP
            </button>
          </div>

          {/* Password field */}
          <div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Password"
                autoComplete="off"
                value={formData.password}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                className={`w-full pr-10 py-3 border-b-2 focus:outline-none ${
                  errors.password ? "border-red-300" : "border-gray-300"
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-3 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="text-red-500 text-xs mt-1">{errors.password}</p>
            )}
            <button
              onClick={handleForgotPassword}
              className="text-black text-sm font-semibold mt-2 float-right"
            >
              Forgot Password
            </button>
          </div>
        </div>

        {/* Action buttons */}
        <div className="mt-8">
          <p className="text-center text-gray-500 text-sm mb-4">
            or continue with
          </p>

          <div className="flex space-x-3 mb-6">
            {/* Signup button */}
            <button className="flex-1 main-btn py-2" onClick={onSwitchToSignup}>
              Signup
            </button>

            {/* Google login button */}
            <button
              onClick={handleModalGoogleLogin}
              disabled={loading}
              className={`p-2 border border-gray-300 bg-white shadow-md rounded-full px-5 transition-colors hover:bg-gray-50 ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <img
                src="https://images.halalfull.com/dev/assets/img/google_logo.svg"
                alt="Google"
                className="w-6 h-6"
              />
            </button>

            {/* Login button */}
            <button
              onClick={handleLogin}
              className="flex-1 main-btn py-2"
              disabled={loading}
            >
              {loading ? <ButtonLoader /> : "Login"}
            </button>
          </div>

          {/* Terms and conditions */}
          <p className="text-xs text-gray-500 text-center">
            By clicking the login button, you agree to Halalfull's
            <a href="/privacy-policy" className="text-red-500 hover:underline">
              {" "}
              privacy policy{" "}
            </a>
            and
            <a href="/" className="text-red-500 hover:underline">
              {" "}
              terms & conditions
            </a>
            .
          </p>
        </div>
      </div>
    </Modal>
  );
};

// ============================================
// Signup Modal Component
// ============================================
export const SignupModal = ({
  isOpen,
  onClose,
  onSwitchToLogin,
  onOTPVerification,
  phone_code,
}) => {
  // Read referral code from URL or fallback to localStorage
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const referralCodeFromUrl = urlParams.get('ref') || localStorage.getItem('referral_code') || '';

  // Local state
  const [formData, setFormData] = useState({
    phone_number: "",
    phone_code: phone_code,
    password: "",
    confirm_password: "",
    action: "signup",
    referral_code: referralCodeFromUrl,
  });

  // Dynamically update referral_code state when modal opens
  useEffect(() => {
    if (isOpen) {
      const currentCode = new URLSearchParams(location.search).get('ref') || localStorage.getItem('referral_code') || '';
      setFormData((prev) => ({
        ...prev,
        referral_code: currentCode,
      }));
    }
  }, [isOpen, location.search]);

  const [showPassword, setShowPassword] = useState(false);
  const [confirmShowPassword, setConfirmShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  // Redux hooks
  const loading = useSelector(selectAuthLoading);
  const dispatch = useDispatch();

  // Google login hook
  const { handleGoogleLogin } = useGoogleLogin();

  // Validate signup form
  const validateForm = () => {
    const newErrors = {};
    if (!formData.phone_number || formData.phone_number.length < 10) {
      newErrors.phone_number = "Please enter a valid mobile number";
    }
    if (!formData.password || formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    if (!formData.confirm_password || formData.confirm_password.length < 6) {
      newErrors.confirm_password =
        "Confirm password must be at least 6 characters";
    } else if (formData.confirm_password !== formData.password) {
      newErrors.confirm_password = "Passwords do not match";
    }
    setErrors(newErrors);
    return newErrors;
  };

  // Handle signup submission
  const handleSignup = () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      Object.values(validationErrors).forEach((msg) => toast.error(msg));
      return;
    }

    const signupPromise = dispatch(signUpUser(formData)).unwrap();

    toast.promise(
      signupPromise.then((res) => {
        if (res.status === 0) {
          throw new Error(res.message || "Signup failed.");
        }

        // Clear referral code from localStorage on successful signup payload submission
        localStorage.removeItem('referral_code');

        // Check if OTP verification is required
        const shouldShowOTP = res.show_otp === 1 || res.show_signup_otp === 1;
        const hasPhone = !!res.phone_number;

        if (shouldShowOTP && hasPhone) {
          const onlyDigits = res.phone_number.replace(/\D/g, "");
          const otpData = {
            phone_number: onlyDigits,
            user_id: res.user_id,
            verification_type: "register",
            mode: "signup-otp",
          };

          onClose();
          onOTPVerification(otpData);
        } else {
          // Direct signup success without OTP
          onClose();
          setFormData({ phone_number: "", password: "", confirm_password: "" });
          setErrors({});
        }

        return res.message || "Signup completed successfully!";
      }),
      {
        loading: "Signing up...",
        success: (msg) => msg,
        error: (err) => {
          setErrors({ general: err.message });
          return err.message || "Signup failed. Please try again.";
        },
      },
      {
        success: { duration: 3000 },
        error: { duration: 4000 },
      }
    );
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({ phone_number: "", password: "", confirm_password: "" });
      setErrors({});
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      {/* Modal header */}
      <div className="bg-red-gradient text-white p-4 rounded-t-lg flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <button onClick={onSwitchToLogin} className="p-1 rounded">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <UserPlus className="w-5 h-5" />
          <h2 className="text-lg font-semibold">Signup</h2>
        </div>
        <button onClick={onClose} className="p-1 rounded">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Modal body */}
      <form
        className="p-6"
        onSubmit={(e) => {
          e.preventDefault();
          handleSignup();
        }}
      >
        <div className="space-y-4">
          {/* Phone number field */}
          <div>
            <div className="relative">
              <PhoneInput
                className="border-b border-gray-300 font-medium bg-white w-full"
                value={formData.phone_number}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, phone_number: value }))
                }
                autoComplete="off"
              />
            </div>
            {errors.phone_number && (
              <p className="text-red-500 text-xs mt-1">{errors.phone_number}</p>
            )}
          </div>

          {/* Password field */}
          <div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={formData.password}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, password: e.target.value }))
                }
                className={`w-full pr-10 py-3 border-b-2 focus:outline-none ${
                  errors.password ? "border-red-300" : "border-gray-300"
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-3 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="text-red-500 text-xs mt-1">{errors.password}</p>
            )}
          </div>

          {/* Confirm password field */}
          <div>
            <div className="relative">
              <input
                type={confirmShowPassword ? "text" : "password"}
                placeholder="Confirm Password"
                value={formData.confirm_password}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    confirm_password: e.target.value,
                  }))
                }
                className={`w-full pr-10 py-3 border-b-2 focus:outline-none ${
                  errors.confirm_password ? "border-red-300" : "border-gray-300"
                }`}
              />
              <button
                type="button"
                onClick={() => setConfirmShowPassword(!confirmShowPassword)}
                className="absolute right-2 top-3 text-gray-500 hover:text-gray-700"
              >
                {confirmShowPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            {errors.confirm_password && (
              <p className="text-red-500 text-xs mt-1">
                {errors.confirm_password}
              </p>
            )}
          </div>
        </div>

        {/* General error message */}
        {errors.general && (
          <p className="text-red-500 text-sm mt-4 text-center">
            {errors.general}
          </p>
        )}

        {/* Action buttons */}
        <div className="mt-8 px-10">
          <button
            type="submit"
            disabled={loading}
            className={`w-full main-btn text-white py-3 text-base shadow-[1px_1px_6px_0_#000000c4] rounded-full font-semibold mb-4 transition-colors ${
              loading ? "opacity-50 cursor-not-allowed" : "hover:bg-red-900"
            }`}
          >
            {loading ? <ButtonLoader /> : "Signup"}
          </button>
          <p className="text-xs text-gray-500 text-center mt-4">
            By clicking the 'SIGNUP' button, you agree to our terms and
            conditions.
          </p>

          {/* Google Signup Section */}
          <div className="mt-6">
            <p className="text-center text-gray-500 text-sm mb-4">
              or continue with
            </p>
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => handleGoogleLogin("/signup")}
                disabled={loading}
                className={`p-3 border border-gray-300 bg-white shadow-md rounded-full hover:bg-gray-50 transition-colors ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <img
                  src="https://images.halalfull.com/dev/assets/img/google_logo.svg"
                  alt="Continue with Google"
                  className="w-6 h-6"
                />
              </button>
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
};

// ============================================
// OTP Verification Modal Component
// ============================================
export const OTPModal = ({
  isOpen,
  onClose,
  onBack,
  phoneNumber,
  type = "login",
  onPasswordReset,
  verificationData,
  onVerificationSuccess,
  onLoginSuccess,
  onCheckoutSuccess,
  isCheckoutMode = false,
  phone_code,
}) => {
  // Local state
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [resendTimer, setResendTimer] = useState(93);
  const [isLoading, setIsLoading] = useState(false);
  const inputRefs = useRef([]);

  // Redux hooks
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);
  const isUserLoggedIn = Boolean(userInfo?.user_id);
  // Extract verification data
  const {
    phone_number = phoneNumber,
    user_id,
    verification_type,
    mode,
  } = verificationData || {};

  // Reset OTP and timer when modal opens
  useEffect(() => {
    if (isOpen) {
      setOtp(["", "", "", "", "", ""]);
      setResendTimer(93);
      setIsLoading(false);
      // Focus first input after modal opens
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Timer countdown effect
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    // Only allow digits and single character
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus next input
      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  // Handle keyboard events
  const handleKeyDown = (index, e) => {
    // Handle backspace to move to previous input
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // Handle paste
    if (e.key === "v" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then((text) => {
        const digits = text.replace(/\D/g, "").slice(0, 6);
        if (digits.length > 0) {
          const newOtp = [...otp];
          for (let i = 0; i < digits.length && i < 6; i++) {
            newOtp[i] = digits[i];
          }
          setOtp(newOtp);
          // Focus the next empty input or last input
          const nextIndex = Math.min(digits.length, 5);
          inputRefs.current[nextIndex]?.focus();
        }
      });
    }
  };

  // Helper function to save partner data to Redux
  const savePartnerDataToSession = (responseData) => {
    if (responseData?.user_details) {
      const userData = responseData?.user_details;
      // Store partner data in Redux
      dispatch(
        setPartnerInfo({
          partnerData: {
            shortName: verificationData?.partnerShortName || "unknown",
            partnerId: verificationData?.partnerId || null,
            name: verificationData?.partnerName || "Unknown Partner",
            address:
              verificationData?.partnerAddress || "Address not available",
          },
          userData: {
            userId: userData.user_id,
            userRole: userData.user_role || verificationData?.userRole,
            phone: userData.phone_number || phone_number,
          },
        })
      );
      return userData;
    }
    return null;
  };

  // Handle OTP verification
  const handleVerifyOtp = async () => {
    const code = otp.join("");
    if (code.length !== 6) {
      toast.error("Please enter the complete 6-digit OTP");
      return;
    }

    setIsLoading(true);

    try {
      const toastId = toast.loading("Verifying OTP...");
      let response;

      // Call appropriate verification API based on mode
      switch (mode) {
        case "login-with-otp": {
          const payload = {
            phone_number: phone_number,
            code,
            phone_code: phone_code,
            user_id: String(user_id),
            verification_type: "login",
          };
          response = await dispatch(verifyOtp(payload)).unwrap();
          break;
        }
        case "forgot-password": {
          const payload = {
            code,
            user_id: String(user_id),
          };
          response = await dispatch(verifyResetCode(payload)).unwrap();
          break;
        }
        case "signup-otp": {
          const payload = {
            phone_number: phone_number,
            code: code,
            phone_code: phone_code,
            verification_type: verification_type || "register",
            user_id: String(user_id),
          };
          response = await dispatch(signupOtpVerification(payload)).unwrap();
          break;
        }
        case "checkout-signup-otp": {
          const payload = {
            phone_number: phone_number,
            code: code,
            phone_code: phone_code,
            verification_type: verification_type || "register",
            user_id: String(user_id),
          };
          response = await dispatch(signupOtpVerification(payload)).unwrap();
          break;
        }
        case "verify-otp": {
          const payload = {
            phone_number: phone_number,
            code: code,
            phone_code: phone_code,
            verification_type: verification_type || "register",
            user_id: String(user_id),
          };
          response = await dispatch(signupOtpVerification(payload)).unwrap();
          break;
        }
        case "partners-verify-otp": {
          const payload = {
            phone_number: phone_number,
            code: code,
            phone_code: phone_code,
            verification_type: verification_type || "register",
            user_id: String(user_id),
          };
          if (isUserLoggedIn) {
            console.log(
              "User already logged in, verifying delivery partner OTP without storing token"
            );
            response = await dispatch(
              signupOtpVerificationDeliveryPartner(payload)
            ).unwrap();
          } else {
            // No user logged in, verify and store token
            console.log("No user logged in, verifying OTP with token storage");
            response = await dispatch(verifyOtp(payload)).unwrap();
          }
          break;
        }
        default:
          throw new Error("Invalid verification mode. Please try again.");
      }

      toast.dismiss(toastId);

      // Handle successful verification
      if (response?.status === 1) {
        toast.success(response.message || "OTP verified successfully!");

        // Reset form
        setOtp(["", "", "", "", "", ""]);
        setResendTimer(93);

        // Handle different verification modes
        switch (mode) {
          case "login-with-otp":
            // Fetch user profile to check role
            try {
              const userProfileResult = await dispatch(
                fetchUserProfile()
              ).unwrap();
              if (userProfileResult?.data?.role_name === "Super Admin") {
                onClose();
                navigate("/orders-list?type=delivery-partner");
                return;
              }
            } catch (error) {
              console.error("Failed to fetch user profile:", error);
            }

            // Handle checkout mode
            if (isCheckoutMode && onLoginSuccess) {
              onLoginSuccess();
            } else {
              onClose();
            }
            break;
          case "signup-otp":
            onClose();
            navigate("/");
            break;
          case "checkout-signup-otp":
            onClose();
            if (onCheckoutSuccess) {
              await onCheckoutSuccess(response);
            }
            break;
          case "verify-otp":
            onClose();
            break;
          case "partners-verify-otp":
            // Save partner data to Redux
            const savedPartnerData = savePartnerDataToSession(response);
            onClose();
            // Call success callback
            if (onVerificationSuccess) {
              onVerificationSuccess(savedPartnerData);
            }
            break;
          case "forgot-password":
            onClose();
            if (onPasswordReset) {
              onPasswordReset({
                user_id: response.user_id || user_id,
                phone_number: phone_number,
                mode: "reset",
              });
            }
            break;
          default:
            onClose();
            navigate("/");
        }
      } else {
        toast.error(
          response?.message || "Invalid OTP. Please check and try again."
        );
      }
    } catch (err) {
      console.error("OTP verification error:", err);
      toast.error(err.message || "OTP verification failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle resend OTP
  const handleResendOtp = async () => {
    if (resendTimer > 0) return;

    try {
      const toastId = toast.loading("Resending OTP...");
      let response;

      // Determine verification type for resend
      switch (mode) {
        case "login-with-otp":
          response = await dispatch(
            sentLoginOtpCode({
              phone_number: phone_number,
              phone_code: phone_code,
              verification_type: "login",
            })
          ).unwrap();
          break;
        case "signup-otp":
        case "partners-verify-otp":
          response = await dispatch(
            sentLoginOtpCode({
              phone_number: phone_number,
              phone_code: phone_code,
              verification_type: "register",
            })
          ).unwrap();
          break;
        case "forgot-password":
          response = await dispatch(
            sentLoginOtpCode({
              phone_number: phone_number,
              phone_code: phone_code,
              verification_type: "forgot",
            })
          ).unwrap();
          break;
        default:
          throw new Error("Invalid resend mode.");
      }

      toast.dismiss(toastId);

      if (response?.status === 1) {
        toast.success("OTP resent successfully!");
        setResendTimer(93);
        setOtp(["", "", "", "", "", ""]);
        // Focus first input after resend
        setTimeout(() => {
          inputRefs.current[0]?.focus();
        }, 100);
      } else {
        toast.error(
          response?.message || "Failed to resend OTP. Please try again."
        );
      }
    } catch (err) {
      console.error("OTP resend error:", err);
      toast.error(err.message || "Failed to resend OTP. Please try again.");
    }
  };

  // Get modal title based on mode
  const getModalTitle = () => {
    switch (mode) {
      case "login-with-otp":
        return "Login Verification";
      case "signup-otp":
        return "Signup Verification";
      case "partners-verify-otp":
        return "Partner Verify OTP";
      case "forgot-password":
        return "Password Reset Verification";
      default:
        return "Verify OTP";
    }
  };

  // Get button text based on mode
  const getButtonText = () => {
    if (isLoading) return "Verifying...";

    switch (mode) {
      case "login-with-otp":
        return "Complete Login";
      case "signup-otp":
        return "Complete Signup";
      case "partners-verify-otp":
        return "Proceed to checkout";
      case "forgot-password":
        return "Verify & Reset Password";
      default:
        return "Verify OTP";
    }
  };

  // Get description text based on mode
  const getDescription = () => {
    switch (mode) {
      case "login-with-otp":
        return "Enter the 6-digit verification code sent to your phone to complete login";
      case "signup-otp":
        return "Enter the 6-digit verification code sent to your phone to complete signup";
      case "partners-verify-otp":
        return "Enter the 6-digit verification code sent to your phone to complete customer registration";
      case "forgot-password":
        return "Enter the 6-digit verification code sent to your phone to reset your password";
      default:
        return "Enter the 6-digit verification code sent to your phone";
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      {/* Modal header */}
      <div className="bg-red-gradient text-white p-4 rounded-t-lg flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {onBack && (
            <button
              onClick={onBack}
              className="hover:bg-red-600 p-1 rounded transition-colors"
              disabled={isLoading}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <UserPlus className="w-5 h-5" />
          <h2 className="text-lg font-semibold">{getModalTitle()}</h2>
        </div>
        <button
          onClick={onClose}
          className="hover:bg-red-600 p-1 rounded transition-colors"
          disabled={isLoading}
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Modal body */}
      <div className="p-6">
        {/* Phone number display */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <span className="text-gray-800 text-xl font-bold">
              {phone_code} {formatPhoneNumber(phone_number)}
            </span>
            {onBack && (
              <button
                onClick={onBack}
                disabled={isLoading}
                className="hover:bg-gray-100 p-1 rounded transition-colors"
              >
                <Pencil className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>
          <p className="text-gray-600 text-sm leading-relaxed">
            {getDescription()}
          </p>
        </div>

        {/* OTP input fields */}
        <div className="flex space-x-2 justify-center mb-6">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength="1"
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              disabled={isLoading}
              className={`w-12 h-12 text-center text-xl font-bold border-b-2 focus:outline-none transition-colors ${
                isLoading
                  ? "border-gray-200 bg-gray-50 cursor-not-allowed"
                  : "border-gray-300 focus:border-red-500"
              }`}
              placeholder="0"
            />
          ))}
        </div>

        {/* Resend OTP button */}
        <div className="text-center mb-6">
          <button
            onClick={handleResendOtp}
            disabled={resendTimer > 0 || isLoading}
            className={`text-sm transition-colors ${
              resendTimer > 0 || isLoading
                ? "text-gray-400 cursor-not-allowed"
                : "text-red-500 hover:text-red-700"
            }`}
          >
            {resendTimer > 0 ? `Resend Code in ${resendTimer}s` : "Resend Code"}
          </button>
        </div>

        {/* Verify button */}
        <button
          onClick={handleVerifyOtp}
          disabled={otp.join("").length !== 6 || isLoading}
          className={`w-full py-3 px-4 rounded-full font-medium transition-colors ${
            otp.join("").length !== 6 || isLoading
              ? "bg-gray-400 text-gray-200 cursor-not-allowed"
              : "bg-red-gradient text-white hover:bg-red-700"
          }`}
        >
          {getButtonText()}
        </button>

        {/* Terms and conditions */}
        <p className="text-xs text-gray-500 text-center mt-4 leading-relaxed">
          By verifying your phone number, you agree to our{" "}
          <a href="/terms" className="text-red-500 hover:underline">
            Terms of Service
          </a>{" "}
          and{" "}
          <a href="/privacy" className="text-red-500 hover:underline">
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </Modal>
  );
};

// ============================================
// Update Password Modal Component
// ============================================
export const UpdatePasswordModal = ({
  isOpen,
  onClose,
  onBack,
  mode = "reset",
  userData = null,
}) => {
  // Local state
  const [formData, setFormData] = useState({
    new_password: "",
    confirm_password: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false,
  });
  const [errors, setErrors] = useState({});

  // Redux hooks
  const loading = useSelector(selectAuthLoading);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({ new_password: "", confirm_password: "" });
      setErrors({});
    }
  }, [isOpen]);

  // Validate password form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.new_password) {
      newErrors.new_password = "New password is required";
    } else if (formData.new_password.length < 6) {
      newErrors.new_password = "Password must be at least 6 characters";
    }

    if (!formData.confirm_password) {
      newErrors.confirm_password = "Please confirm your new password";
    } else if (formData.new_password !== formData.confirm_password) {
      newErrors.confirm_password = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  // Handle password update submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      const firstError = Object.values(errors)[0];
      if (firstError) toast.error(firstError);
      return;
    }

    try {
      const payload = {
        user_id: String(userData?.user_id),
        password: formData.new_password,
        confirm_password: formData.confirm_password,
      };
      const response = await dispatch(updateNewPassword(payload)).unwrap();

      if (response?.status === 1) {
        toast.success(response.message || "Password updated successfully!");
        onClose();

        // Reset form
        setFormData({ new_password: "", confirm_password: "" });
        setErrors({});

        toast.success(
          "Password reset successfully! You can now login with your new password."
        );
      } else {
        toast.error(
          response?.message || "Failed to update password. Please try again."
        );
      }
    } catch (err) {
      console.error("Password update error:", err);
      toast.error(
        err.message || "Failed to update password. Please try again."
      );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      {/* Modal header */}
      <div className="bg-red-gradient text-white p-4 rounded-t-lg flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {onBack && (
            <button
              onClick={onBack}
              className="hover:bg-red-600 p-1 rounded transition-colors"
              disabled={loading}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <Lock className="w-5 h-5" />
          <h2 className="text-lg font-semibold">Set New Password</h2>
        </div>
        <button
          onClick={onClose}
          className="hover:bg-red-600 p-1 rounded transition-colors"
          disabled={loading}
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Modal body */}
      <form onSubmit={handleSubmit} className="p-6">
        <div className="space-y-4">
          {/* New password field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.new ? "text" : "password"}
                name="new_password"
                value={formData.new_password}
                onChange={handleInputChange}
                disabled={loading}
                className={`w-full pr-10 py-3 border-b-2 focus:outline-none transition-colors ${
                  errors.new_password
                    ? "border-red-300 focus:border-red-500"
                    : "border-gray-300 focus:border-red-500"
                } ${loading ? "bg-gray-50 cursor-not-allowed" : ""}`}
                placeholder="Enter your new password"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility("new")}
                disabled={loading}
                className="absolute right-2 top-3 text-gray-500 hover:text-gray-700 transition-colors"
              >
                {showPasswords.new ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            {errors.new_password && (
              <p className="text-red-500 text-xs mt-1">{errors.new_password}</p>
            )}
          </div>

          {/* Confirm password field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? "text" : "password"}
                name="confirm_password"
                value={formData.confirm_password}
                onChange={handleInputChange}
                disabled={loading}
                className={`w-full pr-10 py-3 border-b-2 focus:outline-none transition-colors ${
                  errors.confirm_password
                    ? "border-red-300 focus:border-red-500"
                    : formData.new_password &&
                      formData.confirm_password &&
                      formData.new_password === formData.confirm_password
                    ? "border-green-300 focus:border-green-500"
                    : "border-gray-300 focus:border-red-500"
                } ${loading ? "bg-gray-50 cursor-not-allowed" : ""}`}
                placeholder="Confirm your new password"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility("confirm")}
                disabled={loading}
                className="absolute right-2 top-3 text-gray-500 hover:text-gray-700 transition-colors"
              >
                {showPasswords.confirm ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            {errors.confirm_password && (
              <p className="text-red-500 text-xs mt-1">
                {errors.confirm_password}
              </p>
            )}
            {/* Password match status */}
            {formData.new_password && formData.confirm_password && (
              <p
                className={`text-xs mt-1 ${
                  formData.new_password === formData.confirm_password
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              >
                {formData.new_password === formData.confirm_password
                  ? "✓ Passwords match"
                  : "✗ Passwords do not match"}
              </p>
            )}
          </div>
        </div>

        {/* Submit button */}
        <div className="mt-8">
          <button
            type="submit"
            disabled={loading || Object.keys(errors).length > 0}
            className={`w-full py-3 px-4 rounded-full font-medium transition-colors ${
              loading || Object.keys(errors).length > 0
                ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                : "bg-red-gradient text-white hover:bg-red-700"
            }`}
          >
            {loading ? <ButtonLoader /> : "Set New Password"}
          </button>
        </div>

        {/* Terms */}
        <p className="text-xs text-gray-500 text-center mt-4 leading-relaxed">
          By updating your password, you agree to our{" "}
          <a href="/terms" className="text-red-500 hover:underline">
            Terms of Service
          </a>{" "}
          and{" "}
          <a href="/privacy" className="text-red-500 hover:underline">
            Privacy Policy
          </a>
          .
        </p>
      </form>
    </Modal>
  );
};
